import React from "react";
import {
  Box,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { fieldSx } from "../../../types/constants";

// ─── FormField ────────────────────────────────────────────────────────────────

// Extend TextFieldProps so Autocomplete params spread in with correct types.
// We omit 'onChange' and 'variant' to replace them with our own signatures.
type FormFieldProps = Omit<TextFieldProps, "onChange" | "variant"> & {
  onChange: (v: string) => void;
};

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  function FormField({ onChange, sx, slotProps, ...rest }, ref) {
    return (
      <TextField
        {...rest}
        ref={ref}
        size="small"
        variant="outlined"
        onChange={(e) => onChange(e.target.value)}
        slotProps={{
          ...slotProps,
          inputLabel: { shrink: true },
        }}
        sx={{ ...fieldSx, ...(sx as object) }}
      />
    );
  },
);

// ─── FormSelect ───────────────────────────────────────────────────────────────

interface FormSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  minWidth?: number;
}

export function FormSelect({
  label,
  value,
  onChange,
  options,
  minWidth = 75,
}: FormSelectProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          mb: "6px",
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          borderRadius: "8px",
          fontSize: 14,
          height: 30,
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d9d9d9",
            top: 0,
            "& legend": { display: "none" },
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#bdbdbd",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#8B5A2B",
            borderWidth: "1.5px",
          },
          "& .MuiSelect-select": { padding: "10px 12px" },
        }}
      >
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
